import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import AdminBar from "./AdminBar";
import {
  useSiteContent,
  useWeeklyHours,
  formatDayHours,
  weeklyDayFor,
  DAYS,
  EMPTY_BLOCKED,
  isBlockedDay,
  blockedHintText,
} from "./siteContent";
import { DatePicker } from "./DatePicker";
import { TimeSlotSelect } from "./TimeSlotSelect";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", email: "", date: "", time: "", guests: 2, privatisation: false };

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-4 py-2.5 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

const fieldError = field
  .replace("border-[#102C1F]/25", "border-red-500")
  .replace("focus:border-[#102C1F]", "focus:border-red-500");

const labelCls =
  "mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60";

export default function ReservationPage() {
  const [form, setForm] = useState(EMPTY);
  const site = useSiteContent();
  const weekly = useWeeklyHours();
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const [confirmed, setConfirmed] = useState({ date: "", time: "", guests: 2, privatisation: false });
  const [blockedDays, setBlockedDays] = useState(EMPTY_BLOCKED);
  const [avail, setAvail] = useState(null);
  const [dayAvail, setDayAvail] = useState(null);
  const [serverError, setServerError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/blocked-days/reservations`)
      .then((r) => setBlockedDays({ ...EMPTY_BLOCKED, ...r.data }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.date || !form.time) {
      setAvail(null);
      return;
    }
    let alive = true;
    axios
      .get(`${API}/availability`, {
        params: { date: form.date, time: form.time },
      })
      .then((r) => {
        if (!alive) return;
        setAvail(r.data);
      })
      .catch(() => alive && setAvail(null));
    return () => {
      alive = false;
    };
  }, [form.date, form.time]);

  useEffect(() => {
    if (!form.date) {
      setDayAvail(null);
      return;
    }
    let alive = true;
    axios
      .get(`${API}/availability/day`, { params: { date: form.date } })
      .then((r) => alive && setDayAvail(r.data))
      .catch(() => alive && setDayAvail(null));
    return () => {
      alive = false;
    };
  }, [form.date]);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors((p) => ({ ...p, [key]: false }));
  };

  const missingLabels = [
    errors.name && "Nom et prénom",
    errors.phone && "Téléphone",
    errors.date && "Date",
    errors.time && "Heure",
    errors.guests && "Nombre de personnes",
  ].filter(Boolean);

  const serviceName = (() => {
    if (!form.time) return "";
    const svc = dayAvail?.services?.find((s) =>
      s.slots?.some((sl) => sl.time === form.time)
    );
    if (svc) return svc.name.toLowerCase();
    return form.time < "17:00" ? "déjeuner" : "dîner";
  })();

  const overLimit = !!(
    avail &&
    form.time &&
    Number(form.guests) > avail.remaining
  );

  const fullSlots = (dayAvail?.services || [])
    .filter(
      (s) => s.full || Math.max(0, dayAvail.capacity - s.used) === 0
    )
    .flatMap((s) => (s.slots || []).map((sl) => sl.time));

  const submit = async (e) => {
    e.preventDefault();
    const missing = {
      name: !form.name.trim(),
      phone: !form.phone.trim(),
      date: !form.date,
      time: !form.time || fullSlots.includes(form.time),
      guests: form.privatisation
        ? false
        : !form.guests ||
          Number(form.guests) < 1 ||
          (avail && form.time ? Number(form.guests) > avail.remaining : false),
    };
    if (Object.values(missing).some(Boolean)) {
      setErrors(missing);
      setMissingOpen(true);
      return;
    }
    if (isBlockedDay(blockedDays, form.date)) {
      setErrors((p) => ({ ...p, date: true }));
      toast.error("Jour fermé — choisissez une autre date.");
      return;
    }
    setServerError("");
    setSending(true);
    try {
      await axios.post(`${API}/reservations`, {
        ...form,
        guests: Number(form.guests),
      });
      setConfirmed({
        date: form.date,
        time: form.time,
        guests: form.guests,
        privatisation: form.privatisation,
      });
      setForm(EMPTY);
      setErrors({});
      setConfirmOpen(true);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        "Impossible d'envoyer la réservation. Réessayez.";
      setServerError(detail);
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="reservation-page"
      className="min-h-screen bg-[#F4F1EB] text-[#102C1F]"
    >
      <AdminBar />
      <header className="admin-offset fixed inset-x-0 top-0 z-50 bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4 md:px-10">
          <Link
            to="/"
            data-testid="reservation-back-btn"
            className="flex items-center gap-2 rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
          >
            <ArrowLeft size={14} /> Accueil
          </Link>
          <span className="font-display text-xl italic md:text-2xl">
            J'aime la vie
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-16 pt-24 md:px-10 md:pt-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#102C1F]/60">
            Réserver
          </p>
          <h1 className="font-display text-4xl italic tracking-tight sm:text-5xl">
            {site.reservation_heading}
          </h1>
          <p className="mt-3 text-sm text-[#102C1F]/65 md:text-base">
            {site.reservation_subtext}
          </p>
          <div className="mt-4 flex items-start gap-2 text-xs uppercase tracking-[0.2em] text-[#102C1F]/55">
            <Clock size={14} className="mt-0.5 shrink-0" />
            <div className="space-y-1">
              {DAYS.map(([key, label]) => {
                const jsDay =
                  { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 }[
                    key
                  ];
                const dayBlocked = blockedDays.weekdays.includes(jsDay);
                return (
                  <p
                    key={key}
                    data-testid={`reservation-weekly-${key}`}
                    className={dayBlocked ? "text-red-700/80" : ""}
                  >
                    {label.slice(0, 3)} ·{" "}
                    {dayBlocked ? "Fermé" : formatDayHours(weekly[key])}
                  </p>
                );
              })}
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          noValidate
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 space-y-5"
        >
          <label className="block">
            <span className={labelCls}>Nom et prénom</span>
            <input
              data-testid="reservation-name-input"
              placeholder="Votre nom et prénom"
              value={form.name}
              onChange={set("name")}
              className={errors.name ? fieldError : field}
            />
          </label>
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className={labelCls}>Téléphone</span>
              <input
                data-testid="reservation-phone-input"
                type="tel"
                placeholder="Ex : 06 12 34 56 78"
                value={form.phone}
                onChange={set("phone")}
                className={errors.phone ? fieldError : field}
              />
            </label>
            <label className="block">
              <span className={labelCls}>Email (facultatif)</span>
              <input
                data-testid="reservation-email-input"
                type="email"
                placeholder="email@exemple.fr"
                value={form.email}
                onChange={set("email")}
                className={field}
              />
            </label>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className={labelCls}>Date</span>
              <DatePicker
                testid="reservation-date-input"
                value={form.date}
                onChange={(v) => {
                  setForm((f) => ({ ...f, date: v, time: "" }));
                  setErrors((p) => ({ ...p, date: false }));
                }}
                blocked={blockedDays}
                hasError={errors.date}
              />
              {blockedHintText(blockedDays) && (
                <p
                  data-testid="reservation-blocked-days-hint"
                  className="mt-1.5 text-[11px] text-red-700/80"
                >
                  Fermé : {blockedHintText(blockedDays)}
                </p>
              )}
              {form.date && !isBlockedDay(blockedDays, form.date) && (
                <p
                  data-testid="reservation-day-hours"
                  className="mt-1.5 text-[11px] text-[#102C1F]/60"
                >
                  Ce jour : {formatDayHours(weeklyDayFor(weekly, form.date))}
                </p>
              )}
            </label>
            <label className="block">
              <span className={labelCls}>Heure</span>
              <TimeSlotSelect
                testid="reservation-time-input"
                scope="reservations"
                date={form.date}
                value={form.time}
                onChange={(v) => {
                  setForm((f) => ({ ...f, time: v }));
                  setErrors((p) => ({ ...p, time: false }));
                }}
                hasError={errors.time}
                className={errors.time ? fieldError : field}
                fullSlots={fullSlots}
              />
              {form.time && fullSlots.includes(form.time) && (
                <p
                  data-testid="reservation-time-full-hint"
                  className="mt-1.5 text-[11px] text-red-700/80"
                >
                  Complet pour ce service — choisissez un autre horaire.
                </p>
              )}
            </label>
          </div>
          {form.date && dayAvail && (
            <div
              data-testid="reservation-day-availability"
              className="rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] px-4 py-3"
            >
              <p className="mb-2 text-xs uppercase tracking-[0.2em] text-[#102C1F]/50">
                Disponibilité du jour
              </p>
              {dayAvail.closed || dayAvail.services.length === 0 ? (
                <p className="text-sm text-red-700/80">Fermé ce jour.</p>
              ) : (
                <div className="space-y-1">
                  {dayAvail.services.map((s) => {
                    const remaining = Math.max(0, dayAvail.capacity - s.used);
                    return (
                      <p
                        key={s.name}
                        data-testid={`reservation-day-${s.name === "Déjeuner" ? "dejeuner" : "diner"}`}
                        className="text-sm"
                      >
                        <span className="font-medium">{s.name}</span>
                        <span className="text-[#102C1F]/60">
                          {" "}
                          · {s.used}{" "}
                          {s.used === 1
                            ? "personne réservée"
                            : "personnes réservées"}{" "}
                          ·{" "}
                        </span>
                        {s.privatised || remaining === 0 ? (
                          <span className="font-medium text-red-700">
                            {s.privatised ? "Privatisé" : "Complet"}
                          </span>
                        ) : (
                          <span className="text-[#102C1F]/70">
                            {remaining}{" "}
                            {remaining === 1
                              ? "place restante"
                              : "places restantes"}
                          </span>
                        )}
                      </p>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {!form.privatisation && (
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className={labelCls}>Nombre de personnes</span>
              <select
                data-testid="reservation-guests-input"
                value={form.guests}
                onChange={(e) => {
                  setForm((f) => ({
                    ...f,
                    guests: Number(e.target.value),
                  }));
                  setErrors((p) => ({ ...p, guests: false }));
                }}
                className={errors.guests || overLimit ? fieldError : field}
              >
                {Array.from({ length: 64 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? "personne" : "personnes"}
                  </option>
                ))}
              </select>
              {overLimit ? (
                <p
                  data-testid="reservation-unavailable-hint"
                  className="mt-1.5 text-[11px] text-red-700/80"
                >
                  {avail.remaining === 0
                    ? `Indisponible — complet pour le ${serviceName} à cet horaire.`
                    : `Indisponible pour le ${serviceName} : il ne reste que ${avail.remaining} ${avail.remaining === 1 ? "place" : "places"} à cet horaire.`}
                </p>
              ) : (
                avail &&
                form.date &&
                form.time &&
                avail.remaining > 0 && (
                  <p
                    data-testid="reservation-remaining-hint"
                    className="mt-1.5 text-[11px] text-[#102C1F]/60"
                  >
                    {avail.remaining}{" "}
                    {avail.remaining === 1
                      ? "place restante"
                      : "places restantes"}{" "}
                    à cet horaire
                  </p>
                )
              )}
            </label>
          </div>
          )}
          {serverError && (
            <p
              data-testid="reservation-server-error"
              className="rounded-sm border border-red-700/30 bg-red-700/10 px-4 py-3 text-sm text-red-700"
            >
              {serverError}
            </p>
          )}
          <button
            data-testid="reservation-submit-btn"
            type="submit"
            disabled={sending}
            className="w-full rounded-full bg-[#102C1F] py-4 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030] disabled:opacity-60"
          >
            {sending ? "Envoi..." : "Confirmer la réservation"}
          </button>
        </motion.form>
      </main>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          data-testid="reservation-confirm-popup"
          className="border-[#102C1F]/20 bg-[#F4F1EB] text-center text-[#102C1F] sm:max-w-sm"
        >
          <DialogHeader className="items-center">
            <CheckCircle2 size={48} className="text-[#102C1F]" />
            <DialogTitle className="font-display text-3xl italic tracking-tight">
              Réservation confirmée
            </DialogTitle>
            <DialogDescription className="text-sm text-[#102C1F]/65">
              Merci ! Nous confirmons très vite. Le{" "}
              {new Date(`${confirmed.date}T12:00:00`).toLocaleDateString(
                "fr-FR",
                { weekday: "long", day: "numeric", month: "long" }
              )}{" "}
              à {confirmed.time} ·{" "}
              {confirmed.privatisation
                ? "Restaurant privatisé"
                : `${confirmed.guests} ${Number(confirmed.guests) === 1 ? "personne" : "personnes"}`}
            </DialogDescription>
          </DialogHeader>
          <button
            data-testid="reservation-confirm-ok-btn"
            onClick={() => setConfirmOpen(false)}
            className="mt-2 w-full rounded-full bg-[#102C1F] py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
          >
            OK
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={missingOpen} onOpenChange={setMissingOpen}>
        <DialogContent
          data-testid="reservation-missing-popup"
          className="border-[#102C1F]/20 bg-[#F4F1EB] text-center text-[#102C1F] sm:max-w-sm"
        >
          <DialogHeader className="items-center">
            <AlertTriangle size={48} className="text-red-600" />
            <DialogTitle className="font-display text-3xl italic tracking-tight">
              Il manque quelque chose
            </DialogTitle>
            <DialogDescription className="text-sm text-[#102C1F]/65">
              Veuillez vérifier : {missingLabels.join(", ")}
            </DialogDescription>
          </DialogHeader>
          <button
            data-testid="reservation-missing-ok-btn"
            onClick={() => setMissingOpen(false)}
            className="mt-2 w-full rounded-full bg-[#102C1F] py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
          >
            OK
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
